/* The tab title, and the sentence a screen reader hears after a route change.
   One component, because they are the same string and keeping them in two
   places is how they drift apart.

   A single-page app has to set this itself. The `<title>` served in index.html
   is written once, in English, and then never changes again — so an Arabic
   session ends up with every screen labelled "HexClean — Home services, booked
   in seconds" in the tab strip, in the history menu, in a bookmark, and read
   aloud after every navigation. That is the most-repeated untranslated string
   in the product and the least visible one to anybody testing in English.

   The route table here is deliberately a flat list rather than `useMatch` per
   route: it is read top to bottom, the first prefix wins, and adding a screen
   means adding a line. */

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

/** Longest paths first — `/account/addresses` has to be tested before
 *  `/account`, or it never matches. */
const ROUTES: readonly [prefix: string, key: string][] = [
  ['/account/addresses', 'addresses'],
  ['/account', 'account'],
  ['/app', 'app'],
  ['/book', 'book'],
  ['/explore', 'explore'],
  ['/join', 'join'],
  ['/search', 'search'],
  ['/p/', 'provider'],
  ['/basket', 'basket'],
  ['/checkout', 'checkout'],
  ['/orders', 'orders'],
  ['/order/', 'order'],
  ['/auth/action', 'authAction'],
  ['/auth', 'auth'],
]

function keyFor(pathname: string): string {
  if (pathname === '/') return 'landing'
  return ROUTES.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? 'notFound'
}

export function DocumentTitle() {
  const { pathname } = useLocation()
  const { t, i18n } = useTranslation()

  /* `i18n.language` is in the dependency list on purpose. `t` is a stable
     reference across a language change in react-i18next, so an effect that
     depends only on `t` and the path will not re-run when the locale flips —
     the title would keep whatever language it was first set in until the next
     navigation. */
  useEffect(() => {
    const key = keyFor(pathname)
    const page = t(`title.${key}`)
    /* The landing page's title is the whole pitch and already carries the
       brand; everything else is "Screen · HexClean", which is what makes a row
       of pinned tabs readable. */
    document.title = key === 'landing' ? page : `${page} · ${t('title.suffix')}`

    const region = document.getElementById('route-announcer')
    if (region) region.textContent = document.title
  }, [pathname, t, i18n.language])

  return null
}
